import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as InProgressIcon,
} from '@mui/icons-material';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useLearningLab } from '../../contexts/LearningLabContext';
import { LAB_MISSION_PREREQUISITES } from '../../../shared/onboarding/coordinatorLearningLabTours';
import { filterLabMissionTours } from '../../../shared/onboarding/tourCatalog';
import {
  COORDINATOR_LEARNING_LAB_OFFERING,
  LEARNING_LAB_ENTER_TO_START_HINT,
  LEARNING_LAB_MISSION_DONE_LABEL,
} from './learningLabOfferings';
import { trackEvent } from '../../utils/analytics';
import { ONBOARDING_EVENTS } from '../onboarding/onboardingEventNames';
import type { OnboardingFlow } from '../../types/onboarding';
import {
  shouldShowLearningLabMenuSection,
  type LearningLabNavState,
} from './learningLabNavVisibility';
import { useLearningLabNavContext } from './useLearningLabNav';
import { logger } from '../../utils/logger';
import { coerceTourPlainText } from '../../../shared/utils/coerceEventListingText';

const LAB_MISSION_PREREQUISITE_IDS = [...LAB_MISSION_PREREQUISITES];

function coerceMenuSecondary(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  const text = coerceTourPlainText(value, '');
  return text.length > 0 ? text : undefined;
}

export type LearningLabMenuSectionVariant = 'menu' | 'popover';

export interface LearningLabMenuSectionProps {
  variant: LearningLabMenuSectionVariant;
  navState: LearningLabNavState;
  onActionComplete?: () => void;
  /** Lab banner: list missions only (no enter/exit lab controls). */
  toursOnly?: boolean;
}

export const LearningLabMenuSection: React.FC<LearningLabMenuSectionProps> = ({
  variant,
  navState,
  onActionComplete,
  toursOnly = false,
}) => {
  const navContext = useLearningLabNavContext();
  const { active: labActive, enter: enterLab, exit: exitLab, isStale: labStale } =
    useLearningLab();
  const { state, getAllTours, startTour, isFlowCompleted } = useOnboarding();

  const showSection = shouldShowLearningLabMenuSection(navContext);

  const labTours = useMemo(() => {
    return filterLabMissionTours(getAllTours());
  }, [getAllTours]);

  const missingPrerequisites = useMemo(
    () =>
      LAB_MISSION_PREREQUISITE_IDS.filter(
        (prerequisite) => !isFlowCompleted(prerequisite),
      ),
    [isFlowCompleted],
  );

  const prerequisitesMet = missingPrerequisites.length === 0;

  const prerequisiteHint = useMemo(() => {
    if (prerequisitesMet) {
      return undefined;
    }
    const names = missingPrerequisites
      .map((id) => {
        const rawName =
          labTours.find((t) => t.id === id)?.name ??
          getAllTours().find((t) => t.id === id)?.name ??
          id;
        return coerceTourPlainText(rawName, id);
      })
      .join(' and ');
    return `Complete ${names} first`;
  }, [getAllTours, labTours, missingPrerequisites, prerequisitesMet]);

  const handleStartTour = useCallback(
    async (tourId: string) => {
      if (state.isRunning) {
        return;
      }
      try {
        await startTour(tourId);
        onActionComplete?.();
      } catch (error) {
        logger.error('Error starting lab tour', { error, tourId });
      }
    },
    [onActionComplete, startTour, state.isRunning],
  );

  const trackHeaderLabEvent = useCallback(
    (action: string) => {
      if (variant !== 'popover') {
        return;
      }
      trackEvent({
        action,
        category: 'onboarding',
        label: 'coordinator_learning_lab',
        custom_parameters: { source: 'header' },
      });
    },
    [variant],
  );

  const handleEnter = useCallback(async () => {
    trackHeaderLabEvent(ONBOARDING_EVENTS.LEARNING_LAB_NAV_ENTER_CLICK);
    const ok = await enterLab();
    if (ok) {
      onActionComplete?.();
    }
  }, [enterLab, onActionComplete, trackHeaderLabEvent]);

  const handleExit = useCallback(async () => {
    trackHeaderLabEvent(ONBOARDING_EVENTS.LEARNING_LAB_NAV_EXIT_CLICK);
    const ok = await exitLab();
    if (ok) {
      onActionComplete?.();
    }
  }, [exitLab, onActionComplete, trackHeaderLabEvent]);

  const handleEnterToStartTours = useCallback(async () => {
    trackHeaderLabEvent(ONBOARDING_EVENTS.LEARNING_LAB_NAV_ENTER_CLICK);
    const ok = await enterLab();
    if (ok && variant === 'menu') {
      onActionComplete?.();
    }
  }, [enterLab, onActionComplete, trackHeaderLabEvent, variant]);

  if (!showSection) {
    return null;
  }

  const offering = COORDINATOR_LEARNING_LAB_OFFERING;
  const canEnter =
    navState === 'available' && !labActive;
  const canExit = labActive;
  const enterDisabled = navState === 'impersonationBlocked';

  const renderTourRow = (tour: OnboardingFlow) => {
    const isCompleted = isFlowCompleted(tour.id);
    const isActive = state.currentFlowId === tour.id;
    const lockedByPrereq = !prerequisitesMet && !isCompleted;
    const lockedByLab = !labActive && !toursOnly;
    const locked = lockedByPrereq || lockedByLab;
    const primary = coerceTourPlainText(tour.name, tour.id);
    const secondary = lockedByPrereq
      ? coerceMenuSecondary(prerequisiteHint)
      : lockedByLab
        ? LEARNING_LAB_ENTER_TO_START_HINT
        : (() => {
            if (!tour.estimatedDurationMinutes) {
              return undefined;
            }
            const minutes = coerceTourPlainText(tour.estimatedDurationMinutes, '');
            return minutes ? `${minutes} min` : undefined;
          })();

    if (variant === 'menu') {
      return (
        <MenuItem
          key={tour.id}
          data-testid={`learning-lab-tour-${tour.id}`}
          onClick={() => void handleStartTour(tour.id)}
          disabled={isActive || state.isRunning || locked}
        >
          <ListItemIcon>
            {isActive ? (
              <InProgressIcon color="primary" fontSize="small" />
            ) : isCompleted ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <PlayIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText primary={primary} secondary={secondary} />
          {isCompleted ? (
            <Chip
              label={LEARNING_LAB_MISSION_DONE_LABEL}
              size="small"
              color="success"
              sx={{ ml: 1 }}
            />
          ) : null}
        </MenuItem>
      );
    }

    return (
      <Button
        key={tour.id}
        data-testid={`learning-lab-tour-${tour.id}`}
        fullWidth
        size="small"
        variant="text"
        startIcon={
          isActive ? (
            <InProgressIcon color="primary" fontSize="small" />
          ) : isCompleted ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <PlayIcon fontSize="small" />
          )
        }
        onClick={() => void handleStartTour(tour.id)}
        disabled={isActive || state.isRunning || locked}
        sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 0.75 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2">{primary}</Typography>
          {secondary ? (
            <Typography variant="caption" color="text.secondary">
              {secondary}
            </Typography>
          ) : null}
          {isCompleted ? (
            <Typography variant="caption" color="success.main" display="block">
              {LEARNING_LAB_MISSION_DONE_LABEL}
            </Typography>
          ) : null}
        </Box>
      </Button>
    );
  };

  const sectionHeader = (
    <Box sx={{ px: variant === 'menu' ? 2 : 0, py: 0.75 }}>
      <Typography variant="overline" color="text.secondary">
        {coerceTourPlainText(offering.title, 'Learning Lab practice')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {coerceTourPlainText(offering.description, '')}
      </Typography>
    </Box>
  );

  const enterExitControl =
    variant === 'menu' ? (
      <>
        {!labActive ? (
          <MenuItem
            data-testid="onboarding-menu-enter-lab"
            disabled={enterDisabled}
            onClick={() => void handleEnter()}
          >
            <ListItemText
              primary="Enter Learning Lab"
              secondary="Practice with sample agency data (read-only)"
            />
          </MenuItem>
        ) : (
          <MenuItem
            data-testid="onboarding-menu-exit-lab"
            onClick={() => void handleExit()}
          >
            <ListItemText
              primary="Exit Learning Lab"
              secondary={
                labStale
                  ? 'Lab content updated — re-enter after exit'
                  : 'Return to your agency'
              }
            />
          </MenuItem>
        )}
      </>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {canEnter ? (
          <>
            <Button
              data-testid="learning-lab-nav-enter"
              variant="contained"
              size="small"
              disabled={enterDisabled}
              onClick={() => void handleEnter()}
            >
              Enter Learning Lab
            </Button>
            <Button
              data-testid="learning-lab-nav-enter-for-tours"
              variant="outlined"
              size="small"
              disabled={enterDisabled}
              onClick={() => void handleEnterToStartTours()}
            >
              Enter lab to start tours
            </Button>
          </>
        ) : null}
        {canExit ? (
          <Button
            data-testid="learning-lab-nav-exit"
            variant="contained"
            color="info"
            size="small"
            onClick={() => void handleExit()}
          >
            Exit Learning Lab
          </Button>
        ) : null}
      </Box>
    );

  const tourList =
    labTours.length > 0 ? (
      <>
        {variant === 'menu' ? (
          <>
            <Box sx={{ px: 2, py: 0.75 }}>
              <Typography variant="overline" color="text.secondary">
                Lab missions
              </Typography>
            </Box>
            {labTours.map(renderTourRow)}
          </>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="overline" color="text.secondary">
              Lab missions
            </Typography>
            {labTours.map(renderTourRow)}
          </Box>
        )}
      </>
    ) : null;

  if (variant === 'menu' && toursOnly) {
    if (!labTours.length) {
      return (
        <MenuItem disabled>
          <ListItemText primary="No Learning Lab tours available" />
        </MenuItem>
      );
    }
    return (
      <>
        <Box sx={{ px: 2, py: 0.75 }}>
          <Typography variant="overline" color="text.secondary">
            Learning Lab practice
          </Typography>
          {!labActive ? (
            <Typography variant="caption" color="text.secondary" display="block">
              {LEARNING_LAB_ENTER_TO_START_HINT}
            </Typography>
          ) : null}
        </Box>
        {labTours.map((tour) => {
          const isCompleted = isFlowCompleted(tour.id);
          const isActive = state.currentFlowId === tour.id;
          const locked = !labActive || (!prerequisitesMet && !isCompleted);
          return (
            <MenuItem
              key={tour.id}
              data-testid={`learning-lab-tour-${tour.id}`}
              onClick={() => void handleStartTour(tour.id)}
              disabled={isActive || state.isRunning || locked}
            >
              <ListItemIcon>
                {isActive ? (
                  <InProgressIcon color="primary" fontSize="small" />
                ) : isCompleted ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <PlayIcon fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={coerceTourPlainText(tour.name, tour.id)}
                secondary={
                  !labActive
                    ? LEARNING_LAB_ENTER_TO_START_HINT
                    : !prerequisitesMet && !isCompleted
                      ? coerceMenuSecondary(prerequisiteHint)
                      : undefined
                }
              />
              {isCompleted ? (
                <Chip
                  label={LEARNING_LAB_MISSION_DONE_LABEL}
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              ) : null}
            </MenuItem>
          );
        })}
      </>
    );
  }

  if (variant === 'menu') {
    return (
      <>
        {sectionHeader}
        {enterExitControl}
        {tourList}
        <Divider />
      </>
    );
  }

  return (
    <Box>
      {sectionHeader}
      {enterExitControl}
      {tourList}
    </Box>
  );
};

export default LearningLabMenuSection;
